import { dateTimeNowForExcel } from "../../../utils/format";
import { getNamedRangeFromWorksheet } from "../../workbook";
import { cacheableData, worksheetCache } from "../../workbook/cache";
import { rangeNames, rowNames } from "./rangeNames";

export interface ColumnNameToIndexMap {
  startColumnIndex: number; // The column index of the first column in the report
  [key: string]: number; // The column index of the column with the given property name
}

/** Returns the number of columns in a report row from its column map (max column index + 1). */
export const getReportColumnCount = (columnMap: ColumnNameToIndexMap): number => {
  const dataColumnOffsets = Object.entries(columnMap)
    .filter(([key]) => key !== "startColumnIndex")
    .map(([, value]) => value);
  return dataColumnOffsets.length ? Math.max(...dataColumnOffsets) + 1 : 0;
};

/** Stands in for a missing value: a row with no code, or a code with no description. */
export const BLANK_LABEL = "blank";

/**
 * A row or column label for a coded value: `code - description` when codes are shown, otherwise
 * the description alone. A missing description reads as `BLANK_LABEL`; without a code the
 * description is returned as-is, and may be empty.
 */
export const codeLabel = (code: string | undefined, description: string | undefined, showCodes: boolean): string => {
  if (!code) return description ?? "";
  const shownDescription = description || BLANK_LABEL;
  return showCodes ? `${code} - ${shownDescription}` : shownDescription;
};

export const getReportStartRangeFallbackNames = (insertAtRangeName: string): string[] => [
  insertAtRangeName,
  rowNames.legacyReportRange,
  rowNames.startOfPrint,
];

/** Tries insertAtRangeName, then legacy names; returns first valid range or null. */
export const getReportStartRange = async (
  worksheet: Excel.Worksheet,
  insertAtRangeName: string
): Promise<Excel.Range | null> => {
  const names = getReportStartRangeFallbackNames(insertAtRangeName);
  for (const name of names) {
    const nameItem = worksheet.names.getItemOrNullObject(name);
    await worksheet.context.sync();
    if (!nameItem.isNullObject) {
      try {
        const range = nameItem.getRange();
        range.load("rowIndex, columnIndex, rowCount, columnCount");
        await worksheet.context.sync();
        return range;
      } catch {
        continue;
      }
    }
  }
  return null;
};

export const updateReportDate = async (worksheet: Excel.Worksheet): Promise<void> => {
  const dateRange = await getNamedRangeFromWorksheet(worksheet, rangeNames.date);
  if (!dateRange) {
    return;
  }

  dateRange.values = [[dateTimeNowForExcel()]];
  await worksheet.context.sync();
};

export const getReportOptions = async (
  worksheet: Excel.Worksheet,
  defaults: cacheableData
): Promise<Record<string, any>> => {
  const defaultOptions = defaults || {};
  const cachedOptions = (await worksheetCache.getOptions(worksheet)) || {};
  return { ...defaultOptions, ...cachedOptions };
};
